import React, { useEffect, useState } from "react";
import { api, API, downloadPdf as downloadPdfFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Receipt, Download, Loader2, Search, Bell, Eye, FileCheck2, FolderClosed, Trash2 } from "lucide-react";

const fmtMXN = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));

// Recibos de pago — reutilizado en Dirección y Cobranza.
export default function RecibosPanel({ onPendingChange, onChanged }) {
  const { user } = useAuth();
  const canDelete = !!user && user.role !== "staff" && user.role !== "docente";
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("month"); // day | week | month | year
  const [q, setQ] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [pending, setPending] = useState([]);

  const loadPending = async () => {
    try { const r = await api.get("/payments/pending"); setPending(r.data || []); onPendingChange && onPendingChange(); } catch {}
  };
  const verifyPay = async (p) => {
    try { await api.post(`/payments/${p.id}/verify`); toast.success("Pago verificado y registrado en el expediente del alumno"); loadPending(); load(); onChanged && onChanged(); }
    catch { toast.error("No se pudo verificar"); }
  };
  const rejectPay = async (p) => {
    if (!window.confirm(`¿Rechazar el comprobante de ${p.student_name}?`)) return;
    try { await api.post(`/payments/${p.id}/reject`); toast.success("Comprobante rechazado"); loadPending(); onChanged && onChanged(); }
    catch { toast.error("No se pudo rechazar"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payments");
      setPayments((data || []).filter((p) => p.folio && p.status !== "pending_verification" && p.status !== "rejected"));
    } catch { toast.error("No se pudieron cargar los recibos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); loadPending(); }, []);

  const sectionKey = (dateStr) => {
    const d = new Date(dateStr);
    if (section === "day") return d.toISOString().slice(0, 10);
    if (section === "year") return String(d.getFullYear());
    if (section === "week") {
      const monday = new Date(d);
      monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      return `W:${monday.toISOString().slice(0, 10)}`;
    }
    return d.toISOString().slice(0, 7); // month
  };
  const sectionLabel = (key) => {
    if (section === "day") return new Date(key + "T00:00:00").toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    if (section === "year") return `Año ${key}`;
    if (section === "week") { const m = new Date(key.slice(2) + "T00:00:00"); return `Semana del ${m.toLocaleDateString("es-MX", { day: "numeric", month: "long" })}`; }
    const [y, mo] = key.split("-"); return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("es-MX", { month: "long", year: "numeric" });
  };

  const filtered = payments.filter((p) => !q.trim() || (p.student_name || "").toLowerCase().includes(q.trim().toLowerCase()) || (p.folio || "").toLowerCase().includes(q.trim().toLowerCase()));
  const groups = {};
  filtered.forEach((p) => { const k = sectionKey(p.created_at); (groups[k] = groups[k] || []).push(p); });
  const keys = Object.keys(groups).sort().reverse();

  const dlReceipt = async (p) => {
    try { await downloadPdfFile(`/payments/${p.id}/receipt`, { filename: `recibo_${p.folio}.pdf` }); }
    catch { toast.error("No se pudo descargar el recibo"); }
  };
  const delReceipt = async (p) => {
    if (!window.confirm(`¿Eliminar el recibo #${p.folio} de ${p.student_name || "este alumno"} por ${fmtMXN(p.net_amount ?? p.amount)}?\n\nSe recalculará el estatus de pagos. Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/payments/${p.id}`);
      toast.success("Recibo eliminado. Estatus de pagos recalculado.");
      setPayments((ps) => ps.filter((x) => x.id !== p.id));
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar el recibo"); }
  };
  const dlStatement = async (p) => {
    try { await downloadPdfFile(`/students/${p.student_id}/payment-statement-pdf`, { params: { tz: Intl.DateTimeFormat().resolvedOptions().timeZone }, filename: `expediente_pagos_${(p.student_name || "alumno").replace(/\s+/g, "_")}.pdf` }); }
    catch { toast.error("No se pudo descargar el expediente"); }
  };
  const dlPeriod = async () => {
    setDownloading(true);
    try { await downloadPdfFile("/reports/income-pdf", { params: { period: section, tz: Intl.DateTimeFormat().resolvedOptions().timeZone }, filename: `recibos_${section}.pdf` }); }
    catch { toast.error("No se pudo generar el PDF"); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-6" data-testid="recibos-section">
      {/* Notificación: pagos del portal de padres por verificar */}
      {pending.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md" data-testid="parent-pending-card">
          <div className="px-6 py-4 border-b border-amber-200 flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-600" />
            <h2 className="font-display font-semibold text-lg text-amber-800">Pagos del portal de padres por verificar ({pending.length})</h2>
          </div>
          <div className="divide-y divide-amber-100">
            {pending.map((p) => (
              <div key={p.id} className="px-6 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`parent-pending-row-${p.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-navy font-medium truncate">{p.student_name || "—"}</div>
                  <div className="text-[11px] text-nardo-500 truncate">{p.concept}{p.period ? ` · ${p.period}` : ""} · {fmtMXN(p.net_amount ?? p.amount)} · Registrado: {new Date(p.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {p.proof_token && (
                    <a href={`${API}/payment-proof/${p.proof_token}`} target="_blank" rel="noreferrer" data-testid={`parent-view-proof-${p.id}`} className="inline-flex items-center gap-1 text-[11px] text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-md px-2 py-1.5 transition-colors"><Eye className="w-3 h-3" /> Ver comprobante</a>
                  )}
                  <button onClick={() => verifyPay(p)} data-testid={`parent-verify-${p.id}`} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-[11px] font-medium"><FileCheck2 className="w-3.5 h-3.5" /> Verificar y registrar</button>
                  <button onClick={() => rejectPay(p)} data-testid={`parent-reject-${p.id}`} className="inline-flex items-center gap-1 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-md text-[11px] font-medium">Rechazar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-nardo-200 rounded-md">
      <div className="px-6 py-4 border-b border-nardo-200 flex items-center gap-2 flex-wrap">
        <Receipt className="w-4 h-4 text-navy" />
        <h2 className="font-display font-semibold text-xl text-navy">Recibos de pago</h2>
        <button onClick={dlPeriod} disabled={downloading} data-testid="recibos-period-pdf" className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-md px-2.5 py-1.5 transition-colors disabled:opacity-50">
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF periodo actual
        </button>
      </div>
      <div className="px-6 py-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="inline-flex rounded-md border border-nardo-200 overflow-hidden text-xs" data-testid="recibos-section-toggle">
            {[["day", "Día"], ["week", "Semana"], ["month", "Mes"], ["year", "Año"]].map(([v, l]) => (
              <button key={v} onClick={() => setSection(v)} data-testid={`recibos-tab-${v}`} className={`px-3 py-2 font-medium transition-colors ${section === v ? "bg-navy text-white" : "text-nardo-600 hover:bg-nardo-50"}`}>{l}</button>
            ))}
          </div>
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-nardo-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="recibos-search" placeholder="Buscar alumno o folio…" className="w-full border border-nardo-200 rounded-md pl-9 pr-3 py-2 text-sm outline-none focus:border-navy" />
          </div>
        </div>

        <div className="divide-y divide-nardo-100 border border-nardo-100 rounded-md max-h-[60vh] overflow-y-auto">
          {loading && <div className="px-4 py-8 text-center text-sm text-nardo-500">Cargando…</div>}
          {!loading && keys.length === 0 && <div className="px-4 py-8 text-center text-sm text-nardo-500">Sin recibos para mostrar.</div>}
          {keys.map((k) => {
            const total = groups[k].reduce((s, p) => s + Number(p.net_amount ?? p.amount ?? 0), 0);
            return (
              <div key={k} data-testid={`recibos-group-${k}`}>
                <div className="px-4 py-2 bg-nardo-50 flex items-center justify-between sticky top-0">
                  <span className="text-[11px] uppercase tracking-[0.15em] text-nardo-600 font-medium capitalize">{sectionLabel(k)}</span>
                  <span className="text-xs text-navy font-semibold">{groups[k].length} recibo(s) · {fmtMXN(total)}</span>
                </div>
                {groups[k].map((p) => (
                  <div key={p.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`recibo-row-${p.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-navy font-medium truncate">{p.student_name || "—"} <span className="text-[10px] text-nardo-400 font-mono">#{p.folio}</span></div>
                      <div className="text-[11px] text-nardo-500 truncate">{p.concept}{p.period ? ` · ${p.period}` : ""} · {p.method} · Registrado: {new Date(p.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm text-navy font-semibold hidden sm:inline">{fmtMXN(p.net_amount ?? p.amount)}</span>
                      <button onClick={() => dlReceipt(p)} data-testid={`recibo-pdf-${p.id}`} title="Descargar recibo" className="inline-flex items-center gap-1 text-[11px] text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-md px-2 py-1.5 transition-colors"><Download className="w-3 h-3" /> Recibo</button>
                      <button onClick={() => dlStatement(p)} data-testid={`recibo-expediente-${p.student_id}`} title="Expediente de pagos del alumno" className="inline-flex items-center gap-1 text-[11px] text-nardo-600 border border-nardo-200 hover:border-navy hover:text-navy rounded-md px-2 py-1.5 transition-colors"><FolderClosed className="w-3 h-3" /> Expediente</button>
                      {canDelete && (
                        <button onClick={() => delReceipt(p)} data-testid={`recibo-delete-${p.id}`} title="Eliminar recibo" className="inline-flex items-center gap-1 text-[11px] text-red-600 border border-red-200 hover:bg-red-600 hover:text-white hover:border-red-600 rounded-md px-2 py-1.5 transition-colors"><Trash2 className="w-3 h-3" /> Eliminar</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
}
